import React, { useState, useEffect } from "react";
import { useProduct } from "@/contexts/ProductContext";
import { useBrandManager } from "@/hooks/useBrandManager";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { useTypedTranslation } from "@/lib/i18n/hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Copy } from "lucide-react";
import { Label } from "@/components/ui/label";
import { useBrandTerminology } from "@/hooks/useBrandTerminology";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
} from "@/components/ui/dialog";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import { useBrandProductsEnhanced } from "@/hooks/useBrandProductsEnhanced";
import { ProductTagSelector } from "@/components/brand-products/ProductTagSelector";
import { ProductMoreTagSelector } from "@/components/brand-products/ProductMoreTagSelector";
import { ProductTag3Selector } from "@/components/brand-products/ProductTag3Selector";
import { useBrandProductConfig } from "@/hooks/useBrandProductConfig";

export function GlobalProductDialog() {
  const { isOpen, editingProduct, closeProductDialog } = useProduct();
  const { currentBrandId } = useBrandManager();
  const { user } = useAuth();
  const { getTerminology } = useBrandTerminology();
  const { t } = useTypedTranslation();
  const { enableCategories, enableSubcategories, enableTags, enableMoreTags, enableTag3 } = useBrandProductConfig();
  const { categories, subcategories } = useBrandProductsEnhanced(currentBrandId || '');

  const [formData, setFormData] = useState<{
    landing_page_link: string;
    product: string;
    description: string;
    type: "Product" | "Service" | "Hook";
    funnel_stage: "TofU" | "MofU" | "BofU" | "Conversion";
    category_id: string;
    subcategory_id: string;
    tags: string[];
    more_tags: string[];
    tag3: string[];
    price: string;
    price_range_from: string;
    price_range_to: string;
    status: 'active' | 'inactive' | 'discontinued';
    third_party_product_id: string;
  }>({
    landing_page_link: "",
    product: "",
    description: "",
    type: "Product",
    funnel_stage: "TofU",
    category_id: "",
    subcategory_id: "",
    tags: [],
    more_tags: [],
    tag3: [],
    price: "",
    price_range_from: "",
    price_range_to: "",
    status: "active",
    third_party_product_id: "",
  });

  useEffect(() => {
    if (editingProduct && isOpen) {
      const parseJsonArray = (value: any): string[] => {
        if (Array.isArray(value)) return value;
        if (typeof value === 'string') {
          try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        }
        return [];
      };

      setFormData({
        landing_page_link: editingProduct.landing_page_link || "",
        product: editingProduct.product,
        description: editingProduct.description || "",
        type: editingProduct.type,
        funnel_stage: editingProduct.funnel_stage,
        category_id: editingProduct.category_id || "",
        subcategory_id: editingProduct.subcategory_id || "",
        tags: parseJsonArray(editingProduct.tags),
        more_tags: parseJsonArray(editingProduct.more_tags),
        tag3: parseJsonArray(editingProduct.tag3),
        price: editingProduct.price?.toString() || "",
        price_range_from: editingProduct.price_range_from?.toString() || "",
        price_range_to: editingProduct.price_range_to?.toString() || "",
        status: editingProduct.status,
        third_party_product_id: editingProduct.third_party_product_id || "",
      });
    } else if (!isOpen) {
      resetForm();
    }
  }, [editingProduct, isOpen]);

  const resetForm = () => {
    setFormData({
      landing_page_link: "",
      product: "",
      description: "",
      type: "Product",
      funnel_stage: "TofU",
      category_id: "",
      subcategory_id: "",
      tags: [],
      more_tags: [],
      tag3: [],
      price: "",
      price_range_from: "",
      price_range_to: "",
      status: "active",
      third_party_product_id: "",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentBrandId || !user?.id) return;

    try {
      const submitData = {
        ...formData,
        price: formData.price ? parseFloat(formData.price) : null,
        price_range_from: formData.price_range_from ? parseFloat(formData.price_range_from) : null,
        price_range_to: formData.price_range_to ? parseFloat(formData.price_range_to) : null,
        category_id: formData.category_id || null,
        subcategory_id: formData.subcategory_id || null,
        tags: JSON.stringify(formData.tags),
        more_tags: JSON.stringify(formData.more_tags),
      };

      if (editingProduct) {
        const { error } = await supabase
          .from("brand_products_hooks")
          .update({
            ...submitData,
            updated_at: new Date().toISOString(),
          })
          .eq("id", editingProduct.id);

        if (error) throw error;
        toast({
          title: t('brandSetup.brandProducts.toast.success'),
          description: t('brandSetup.brandProducts.toast.updated'),
        });
      } else {
        if (!currentBrandId) {
          throw new Error(t('brandSetup.brandProducts.toast.noBrandSelected'));
        }
        if (!user?.id) {
          throw new Error(t('brandSetup.brandProducts.toast.notAuthenticated'));
        }

        const { error } = await supabase
          .from("brand_products_hooks")
          .insert({
            ...submitData,
            brand_id: currentBrandId,
            user_id: user.id,
          });

        if (error) throw error;
        toast({
          title: t('brandSetup.brandProducts.toast.success'),
          description: t('brandSetup.brandProducts.toast.created'),
        });
      }

      closeProductDialog();
    } catch (error) {
      console.error("Error saving product:", error);
      toast({
        title: t('brandSetup.brandProducts.toast.error'),
        description: t('brandSetup.brandProducts.toast.saveFailed'),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={closeProductDialog} modal={true}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitleWithBug
            popupName={t('brandSetup.brandProducts.dialog.title')}
            popupContext="/brand-products"
            isOpen={isOpen}
          >
            {editingProduct ? t('brandSetup.brandProducts.dialog.editTitle') : t('brandSetup.brandProducts.dialog.addTitle')}
          </DialogTitleWithBug>
          {editingProduct && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span>ID: {editingProduct.id}</span>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(editingProduct.id);
                  toast({
                    title: "Copied",
                    description: "Product ID copied to clipboard",
                  });
                }}
                className="p-0.5 hover:bg-muted rounded transition-colors"
                title="Copy ID to clipboard"
              >
                <Copy className="h-3 w-3" />
              </button>
            </div>
          )}
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="product">{t('brandSetup.brandProducts.dialog.productNameRequired')}</Label>
            <Input
              id="product"
              value={formData.product}
              onChange={(e) => setFormData({ ...formData, product: e.target.value })}
              required
            />
          </div>
          <div>
            <Label htmlFor="type">{t('brandSetup.brandProducts.dialog.typeRequired')}</Label>
            <Select
              value={formData.type}
              onValueChange={(value: "Product" | "Service" | "Hook") =>
                setFormData({ ...formData, type: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Product">{t('brandSetup.brandProducts.dialog.typeProduct')}</SelectItem>
                <SelectItem value="Service">{t('brandSetup.brandProducts.dialog.typeService')}</SelectItem>
                <SelectItem value="Hook">{t('brandSetup.brandProducts.dialog.typeHook')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="funnel_stage">{t('brandSetup.brandProducts.dialog.funnelStageRequired')}</Label>
            <Select
              value={formData.funnel_stage}
              onValueChange={(value: "TofU" | "MofU" | "BofU" | "Conversion") =>
                setFormData({ ...formData, funnel_stage: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TofU">{t('brandSetup.brandProducts.dialog.funnelStageTofU')}</SelectItem>
                <SelectItem value="MofU">{t('brandSetup.brandProducts.dialog.funnelStageMofU')}</SelectItem>
                <SelectItem value="BofU">{t('brandSetup.brandProducts.dialog.funnelStageBofU')}</SelectItem>
                <SelectItem value="Conversion">{t('brandSetup.brandProducts.dialog.funnelStageConversion')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="landing_page_link">{t('brandSetup.brandProducts.dialog.landingPageLink')}</Label>
            <Input
              id="landing_page_link"
              type="url"
              value={formData.landing_page_link}
              onChange={(e) => setFormData({ ...formData, landing_page_link: e.target.value })}
              placeholder={t('brandSetup.brandProducts.dialog.landingPageLinkPlaceholder')}
            />
          </div>
          <div>
            <Label htmlFor="description">{t('brandSetup.brandProducts.dialog.description')}</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>
          
          {enableCategories && (
            <div>
              <Label htmlFor="category">{getTerminology('categories', { plural: false })}</Label>
              <Select
                value={formData.category_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, category_id: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('brandSetup.brandProducts.dialog.selectCategory', { category: getTerminology('categories', { plural: false }).toLowerCase() })} />
                </SelectTrigger>
                <SelectContent className="max-h-[200px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                  <SelectItem value="none">{t('brandSetup.brandProducts.dialog.noCategory', { category: getTerminology('categories', { plural: false }) })}</SelectItem>
                  {categories?.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: category.color }}
                        />
                        {category.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {enableSubcategories && (
            <div>
              <Label htmlFor="subcategory">{getTerminology('subcategories', { plural: false })}</Label>
              <Select
                value={formData.subcategory_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, subcategory_id: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('brandSetup.brandProducts.dialog.selectCategory', { category: getTerminology('subcategories', { plural: false }).toLowerCase() })} />
                </SelectTrigger>
                <SelectContent className="max-h-[200px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                  <SelectItem value="none">{t('brandSetup.brandProducts.dialog.noCategory', { category: getTerminology('subcategories', { plural: false }) })}</SelectItem>
                  {subcategories?.map((subcategory) => (
                    <SelectItem key={subcategory.id} value={subcategory.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: subcategory.color }}
                        />
                        {subcategory.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="price">{t('brandSetup.brandProducts.dialog.price')}</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder={t('brandSetup.brandProducts.dialog.pricePlaceholder')}
              />
            </div>
            <div>
              <Label htmlFor="price_range_from">{t('brandSetup.brandProducts.dialog.priceRangeFrom')}</Label>
              <Input
                id="price_range_from"
                type="number"
                step="0.01"
                value={formData.price_range_from}
                onChange={(e) => setFormData({ ...formData, price_range_from: e.target.value })}
                placeholder={t('brandSetup.brandProducts.dialog.priceRangeFromPlaceholder')}
              />
            </div>
            <div>
              <Label htmlFor="price_range_to">{t('brandSetup.brandProducts.dialog.priceRangeTo')}</Label>
              <Input
                id="price_range_to"
                type="number"
                step="0.01"
                value={formData.price_range_to}
                onChange={(e) => setFormData({ ...formData, price_range_to: e.target.value })}
                placeholder={t('brandSetup.brandProducts.dialog.priceRangeToPlaceholder')}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="status">{t('brandSetup.brandProducts.dialog.status')}</Label>
            <Select
              value={formData.status}
              onValueChange={(value: 'active' | 'inactive' | 'discontinued') =>
                setFormData({ ...formData, status: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">{t('brandSetup.brandProducts.dialog.statusActive')}</SelectItem>
                <SelectItem value="inactive">{t('brandSetup.brandProducts.dialog.statusInactive')}</SelectItem>
                <SelectItem value="discontinued">{t('brandSetup.brandProducts.dialog.statusDiscontinued')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="third_party_product_id">{t('brandSetup.brandProducts.dialog.thirdPartyProductId')}</Label>
            <Input
              id="third_party_product_id"
              value={formData.third_party_product_id}
              onChange={(e) => setFormData({ ...formData, third_party_product_id: e.target.value })}
              placeholder={t('brandSetup.brandProducts.dialog.thirdPartyProductIdPlaceholder')}
            />
          </div>
          
          {enableTags && currentBrandId && (
            <ProductTagSelector
              brandId={currentBrandId}
              selectedTagIds={formData.tags}
              onTagsChange={(tagIds) => setFormData({ ...formData, tags: tagIds })}
            />
          )}
          
          {enableMoreTags && currentBrandId && (
            <ProductMoreTagSelector
              brandId={currentBrandId}
              selectedTagIds={formData.more_tags}
              onTagsChange={(tagIds) => setFormData({ ...formData, more_tags: tagIds })}
            />
          )}
          
          {enableTag3 && currentBrandId && (
            <ProductTag3Selector
              brandId={currentBrandId}
              selectedTagIds={formData.tag3}
              onTagsChange={(tagIds) => setFormData({ ...formData, tag3: tagIds })}
            />
          )}
          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={closeProductDialog}
            >
              {t('brandSetup.brandProducts.dialog.cancel')}
            </Button>
            <Button type="submit">
              {editingProduct ? t('brandSetup.brandProducts.dialog.update') : t('brandSetup.brandProducts.dialog.create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
